import { AgendaFiltersState, AgendaVisitTypeFilter } from "./agendaTypes";
import { clampAgendaScheduleDate } from "./agendaWeekDateRange";

const DEFAULT_VISIT_TYPE: AgendaVisitTypeFilter = "all";

function parseVisitType(
  raw: string | null,
  fallback: AgendaVisitTypeFilter = DEFAULT_VISIT_TYPE
): AgendaVisitTypeFilter {
  if (raw === "presential" || raw === "video") {
    return raw;
  }
  return raw == null ? fallback : DEFAULT_VISIT_TYPE;
}

function parseOptionalInt(raw: string | null): number | undefined {
  if (!raw) {
    return undefined;
  }
  const value = Number(raw);
  return Number.isFinite(value) ? value : undefined;
}

export function formatDateIsoInFortaleza(date: Date): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Fortaleza",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

export function getFortalezaTodayIso(): string {
  return formatDateIsoInFortaleza(new Date());
}

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export function normalizeAgendaIsoDate(raw: string): string | null {
  const trimmed = raw.trim();
  if (ISO_DATE_PATTERN.test(trimmed)) {
    return trimmed;
  }

  const datePrefix = trimmed.slice(0, 10);
  if (ISO_DATE_PATTERN.test(datePrefix)) {
    return datePrefix;
  }

  return null;
}

export function hasExplicitAgendaDateInSearch(search: string): boolean {
  const params = new URLSearchParams(search);
  const dateParam = params.get("scheduleDate") ?? params.get("date");
  if (!dateParam) {
    return false;
  }
  return normalizeAgendaIsoDate(dateParam) != null;
}

export function parseAgendaFiltersFromSearch(
  search: string,
  defaults?: Partial<AgendaFiltersState>,
  options?: { todayIso?: string }
): AgendaFiltersState {
  const params = new URLSearchParams(search);
  const todayIso = options?.todayIso ?? getFortalezaTodayIso();
  const dateParam = params.get("scheduleDate") ?? params.get("date");
  const parsedDate = dateParam ? normalizeAgendaIsoDate(dateParam) : null;
  const rawDate = parsedDate ?? defaults?.date ?? todayIso;
  const date = clampAgendaScheduleDate(rawDate, todayIso);

  const cityParam = params.get("city");
  const cityRaw = cityParam?.trim();
  const city = cityParam === null
    ? defaults?.city
    : cityRaw && cityRaw.toLowerCase() !== "all"
      ? cityRaw
      : undefined;

  const consultantParam = params.get("consultant");
  const teamParam = params.get("teamId");

  return {
    date,
    city,
    visitType: parseVisitType(params.get("type"), defaults?.visitType),
    consultantId:
      consultantParam === null
        ? defaults?.consultantId
        : parseOptionalInt(consultantParam),
    teamId:
      teamParam === null ? defaults?.teamId : parseOptionalInt(teamParam),
  };
}

export function buildAgendaSearch(filters: AgendaFiltersState): string {
  const params = new URLSearchParams();
  params.set("scheduleDate", filters.date);

  if (filters.city) {
    params.set("city", filters.city);
  } else {
    // Keep an explicit marker so a cleared city filter cannot be replaced by
    // the previously persisted city while the URL is parsed again.
    params.set("city", "all");
  }
  if (filters.visitType !== DEFAULT_VISIT_TYPE) {
    params.set("type", filters.visitType);
  }
  if (filters.consultantId != null) {
    params.set("consultant", String(filters.consultantId));
  }
  if (filters.teamId != null) {
    params.set("teamId", String(filters.teamId));
  }

  const query = params.toString();
  return query ? `?${query}` : "";
}

export function mergeAgendaFilters(
  current: AgendaFiltersState,
  patch: Partial<AgendaFiltersState>
): AgendaFiltersState {
  return {
    ...current,
    ...patch,
    visitType: patch.visitType ?? current.visitType,
    date: patch.date ?? current.date,
  };
}
